import type {ClipboardEvent} from 'react';
import {unified} from 'unified';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import rehypeStringify from 'rehype-stringify';

export async function copyMarkdownToClipboard(markdown: string): Promise<void> {
  const result = await unified()
    .use(remarkParse)
    .use(remarkRehype)
    .use(rehypeStringify)
    .process(markdown);

  const html = String(result);
  const blobHtml = new Blob([html], {type: 'text/html'});
  const blobText = new Blob([markdown], {type: 'text/plain'});

  await navigator.clipboard.write([
    new ClipboardItem({'text/html': blobHtml, 'text/plain': blobText}),
  ]);
}

/**
 * Strips padding from non-semantic wrapper divs in copied chat content so email
 * clients (e.g. Outlook) don't inherit padding from our chat UI layout.
 * Only targets plain divs (no semantic role) — preserves padding on content
 * elements like blockquotes, code blocks, etc.
 */
export function handleCopyChatContent(e: ClipboardEvent<HTMLDivElement>): void {
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed) {
    return;
  }

  const range = selection.getRangeAt(0);
  const fragment = range.cloneContents();

  const wrapper = document.createElement('div');
  wrapper.appendChild(fragment);

  // Unwrap plain wrapper divs — these are layout containers from our chat UI,
  // not semantic content. Replace each div with its children so email clients
  // only receive the actual content elements (p, ul, blockquote, etc.)
  wrapper.querySelectorAll('div').forEach((el) => {
    el.replaceWith(...el.childNodes);
  });

  e.clipboardData.setData('text/html', wrapper.innerHTML);
  e.clipboardData.setData('text/plain', selection.toString());
  e.preventDefault();
}
